import { createFileRoute } from '@tanstack/react-router';
import { useState, useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  ColumnDef,
  flexRender,
} from '@tanstack/react-table';
import { trpc } from '@/lib/trpc.ts';
import { CustomButton } from '@/components/uiWrappers/button.tsx';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationPrevious,
  PaginationNext,
  PaginationEllipsis,
} from '@/components/ui/pagination';
import { GoUpload } from "react-icons/go";
import { College } from '@mightybyte/business';
import { UniversitySelectDialog } from '@/components/admin/UniversitySelectDialog';

export const Route = createFileRoute('/admin/user-management')({
  component: UserManagementPage,
});

// User type inferred from the API response
type User = {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  parentRole?: string | null;
  stateOfResidence?: string | null;
  college?: string | null;
  numberOfChildren?: number | null;
  studentGender?: string | null;
  studentFirstName?: string | null;
  studentPhoneLastFour?: string | null;
  completedQuizzesCount?: number;
  totalArticleReads?: number;
  uniqueArticlesRead?: number;
};

// Helper function to get pinning styles
function getCommonPinningStyles(column: any, isPinned: boolean): React.CSSProperties {
  return {
    position: isPinned ? 'sticky' : 'relative',
    left: isPinned ? 0 : undefined,
    width: column.getSize(),
    zIndex: isPinned ? 10 : 1,
    boxShadow: isPinned ? '2px 0 5px -2px rgba(0,0,0,0.1)' : undefined,
  };
}

function UserManagementPage() {
  const [page, setPage] = useState(1);
  const [searchString, setSearchString] = useState('');
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const pageSize = 10;

  const { data, isLoading } = trpc.admin.user.getPage.useQuery({
    page,
    pageSize,
    searchString: searchString || undefined,
  });

  const trpcUtils = trpc.useUtils();

  const handleExport = async (college?: College) => {
    setIsExporting(true);
    try {
      const csvData = await trpcUtils.admin.user.exportUsersCSV.fetch({
        college,
      });

      if (csvData) {
        // Create timestamp for filename
        const now = new Date();
        const timestamp = now.toISOString().replace(/[:.]/g, '-').slice(0, -5); // Format: 2024-01-15T14-30-45

        // Create blob and download
        const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        const fileName = college
          ? `users_${college}_${timestamp}.csv`
          : `users_all_${timestamp}.csv`;
        link.setAttribute('download', fileName);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setIsExporting(false);
      setIsExportDialogOpen(false);
    }
  };

  const users = useMemo(() => {
    return data?.items || [];
  }, [data?.items]);

  const totalPages = useMemo(() => {
    if (!data?.totalItems) return 1;
    return Math.ceil(data.totalItems / pageSize);
  }, [data?.totalItems]);

  const columns = useMemo<ColumnDef<User>[]>(
    () => [
      {
        id: 'userName',
        accessorFn: (row) => {
          if (row.firstName && row.lastName) {
            return `${row.firstName} ${row.lastName}`;
          }
          return row.firstName || row.lastName || '-';
        },
        header: 'User Name',
        size: 200,
      },
      {
        id: 'email',
        accessorKey: 'email',
        header: 'Email Id',
        cell: (info) => info.getValue() || '-',
      },
      {
        id: 'parentRole',
        accessorKey: 'parentRole',
        header: 'Parent Role',
        cell: (info) => info.getValue() || '-',
      },
      {
        id: 'state',
        accessorKey: 'stateOfResidence',
        header: 'State',
        cell: (info) => info.getValue() || '-',
      },
      {
        id: 'college',
        accessorKey: 'college',
        header: 'College',
        cell: (info) => info.getValue() || '-',
      },
      {
        id: 'numberOfChildren',
        accessorKey: 'numberOfChildren',
        header: '# of Children',
        cell: (info) => info.getValue() || '-',
      },
      {
        id: 'studentGender',
        accessorKey: 'studentGender',
        header: 'Student Gender',
        cell: (info) => info.getValue() || '-',
      },
      {
        id: 'studentFirstName',
        accessorKey: 'studentFirstName',
        header: 'Student First Name',
        cell: (info) => info.getValue() || '-',
      },
      {
        id: 'studentPhoneLastFour',
        accessorKey: 'studentPhoneLastFour',
        header: 'Student Phone (last 4)',
        cell: (info) => info.getValue() || '-',
      },
      {
        id: 'quizzes',
        accessorKey: 'completedQuizzesCount',
        header: '# of Quizzes',
        cell: (info) => {
          const value = info.getValue() as number | undefined;
          return <span className="text-brand">{value ?? 0}</span>;
        },
      },
      {
        id: 'articles',
        header: 'Articles open (total/unique)',
        cell: (info) => {
          const row = info.row.original;
          const total = row.totalArticleReads ?? 0;
          const unique = row.uniqueArticlesRead ?? 0;
          return <span className="text-brand">{total}/{unique}</span>;
        },
      },
    ],
    []
  );

  const table = useReactTable({
    data: users,
    columns,
    getCoreRowModel: getCoreRowModel(),
    state: {
      columnPinning: {
        left: ['userName'],
      },
    },
    enableColumnPinning: true,
  });

  const getPageNumbers = () => {
    const pages: (number | 'ellipsis')[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    pages.push(1);

    if (page > 3) {
      pages.push('ellipsis');
    }

    const start = Math.max(2, page - 1);
    const end = Math.min(totalPages - 1, page + 1);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (page < totalPages - 2) {
      pages.push('ellipsis');
    }

    if (totalPages > 1) {
      pages.push(totalPages);
    }

    return pages;
  };

  return (
    <div className="flex-1 min-w-0">
      <div className="bg-white min-w-0">
        {/* Section Header */}
        <div className="pr-4 pb-4 border-b border-border flex justify-between items-center">
          <h2 className="text-xl font-bold text-foreground">All Users</h2>

          <CustomButton
            type="button"
            onClick={() => setIsExportDialogOpen(true)}
            variant="outline"
            size="lg"
            className="rounded-lg flex items-center gap-2"
          >
            <GoUpload
              className="size-5 text-primary"
              data-icon="inline-start"
            />
            Export
          </CustomButton>
        </div>

        {/* Export Dialog */}
        <UniversitySelectDialog
          open={isExportDialogOpen}
          onOpenChange={setIsExportDialogOpen}
          onSelect={handleExport}
          isLoading={isExporting}
          title="Export Users"
          description="Choose which users to export to CSV"
          allLabel="All Users"
        />

        <div className={'flex flex-col justify-between'}>
          <div className="overflow-x-auto max-w-full">
            {isLoading ? (
                <div className="p-12 text-center">
                  <p className="text-gray-500">Loading users...</p>
                </div>
            ) : (
                <Table>
                  <TableHeader>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <TableRow key={headerGroup.id}>
                          {headerGroup.headers.map((header) => {
                            const isPinned = header.column.getIsPinned() === 'left';
                            return (
                                <TableHead
                                    key={header.id}
                                    className="bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wider"
                                    style={getCommonPinningStyles(header.column, isPinned)}
                                >
                                  {header.isPlaceholder
                                      ? null
                                      : flexRender(
                                          header.column.columnDef.header,
                                          header.getContext()
                                      )}
                                </TableHead>
                            );
                          })}
                        </TableRow>
                    ))}
                  </TableHeader>
                  <TableBody>
                    {table.getRowModel().rows.length > 0 ? (
                        table.getRowModel().rows.map((row) => (
                            <TableRow
                                key={row.id}
                                className="group hover:bg-gray-50"
                            >
                              {row.getVisibleCells().map((cell) => {
                                const isPinned = cell.column.getIsPinned() === 'left';
                                const isNameColumn = cell.column.id === 'userName';
                                return (
                                    <TableCell
                                        key={cell.id}
                                        className={`whitespace-nowrap bg-white ${
                                            isNameColumn
                                                ? 'text-sm font-semibold text-gray-900'
                                                : 'text-sm font-medium text-gray-700'
                                        } group-hover:bg-gray-50`}
                                        style={getCommonPinningStyles(cell.column, isPinned)}
                                    >
                                      {flexRender(
                                          cell.column.columnDef.cell,
                                          cell.getContext()
                                      )}
                                    </TableCell>
                                );
                              })}
                            </TableRow>
                        ))
                    ) : (
                        <TableRow>
                          <TableCell
                              colSpan={columns.length}
                              className="h-24 text-center"
                          >
                            No users found.
                          </TableCell>
                        </TableRow>
                    )}
                  </TableBody>
                </Table>
            )}
          </div>

          {/* Pagination */}
          <div className="px-6 pt-4 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-500">
              {data?.totalItems
                  ? `Showing ${(page - 1) * pageSize + 1}-${Math.min(page * pageSize, data.totalItems)} of ${data.totalItems} entries`
                  : 'No entries'}
            </div>

            <Pagination className="w-auto ml-0 mr-0">
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        if (page > 1 && !isLoading) {
                          setPage((p) => Math.max(1, p - 1));
                        }
                      }}
                      className={page === 1 || isLoading ? 'pointer-events-none opacity-50' : ''}
                  />
                </PaginationItem>

                {getPageNumbers().map((pageNum, idx) => (
                    pageNum === 'ellipsis' ? (
                        <PaginationItem key={`ellipsis-${idx}`}>
                          <PaginationEllipsis />
                        </PaginationItem>
                    ) : (
                        <PaginationItem key={pageNum}>
                          <PaginationLink
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                if (!isLoading) {
                                  setPage(pageNum);
                                }
                              }}
                              isActive={page === pageNum}
                              className={isLoading ? 'pointer-events-none opacity-50' : ''}
                          >
                            {pageNum}
                          </PaginationLink>
                        </PaginationItem>
                    )
                ))}

                <PaginationItem>
                  <PaginationNext
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        if (page < totalPages && !isLoading) {
                          setPage((p) => Math.min(totalPages, p + 1));
                        }
                      }}
                      className={page >= totalPages || isLoading ? 'pointer-events-none opacity-50' : ''}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        </div>
        {/* Table */}

      </div>
    </div>
  );
}
